import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { RootState, AppDispatch } from '../app/store'
import toast from 'react-hot-toast'
import { logError, logWarning, log } from '../utils/logger'

// Import components
import ProductDetailsModal from '../components/product/ProductDetailsModal'

// Import home components
import CustomerInfoHeader from '../components/home/CustomerInfoHeader'
import OrderTypeToggle from '../components/home/OrderTypeToggle'
import TableSelector from '../components/home/TableSelector'
import AddressSelector from '../components/home/AddressSelector'
import AddressSelectionModal from '../components/home/AddressSelectionModal'
import TableSelectionModal from '../components/table/TableSelectionModal'
import CartItemsSection from '../components/home/CartItemsSection'
import DiscountNotesSection from '../components/home/DiscountNotesSection'
import NotesModal from '../components/home/NotesModal'
import DiscountModal from '../components/home/DiscountModal'
import OfferConfirmationModal from '../components/home/OfferConfirmationModal'
import OrderSummarySection from '../components/home/OrderSummarySection'
import PaymentMethodModal from '../components/payment/PaymentMethodModal'
import TopBar from '../components/home/TopBar'
import SearchBarSection from '../components/home/SearchBarSection'
import CategoryTabs from '../components/home/CategoryTabs'
import ProductsGridSection from '../components/home/ProductsGridSection'
import AddCustomerAlertModal from '../components/home/AddCustomerAlertModal'
import AddProductsAlertModal from '../components/home/AddProductsAlertModal'
import CSPlaceholder from '../components/home/CSPlaceholder'

// Import CS Flow components
import CsOrderTypeSelectionPopup from '../components/csFlow/CsOrderTypeSelectionPopup'

// Import shared components
import OrderSubmissionLoading from '../components/shared/OrderSubmissionLoading'

// Import actions
import { setOrderTypeLocally, setTableLocally, setPaymentMethod, setNotes, clearDeliveryAddressLocally } from '../features/cart/cartSlice'
import { selectCategory, updateSearchQuery, setUserTypeId } from '../features/menu/menuSlice'
import { selectCartItems, selectCartInfo, loadCartItems } from '../features/cart/cartItemsSlice'
import { createOrder, fetchOrderDetails } from '../features/orders/ordersSlice'
import { openModal, setDiscountData, clearDiscount } from '../features/discount/discountSlice'
import { openModal as openCustomerModal } from '../features/customer/customerSlice'
import { clearAllDataAfterSuccessfulOrder } from '../utils/clearOrderData'
import { evaluateCartOffer } from '../features/offers/offersSlice'
import {
  emptyPromotionEvaluation,
  type PromotionEvaluation,
} from '../features/offers/promotionEvaluation'
import { createPaymobPaymentLinkInBackground } from '../services/paymobService'
import {
  selectIsCsFlowComplete,
  selectSelectedTypeId,
  loadOrderTypes,
  loadSavedBranchData,
  markBranchSelected,
} from '../features/csFlow/csFlowSlice'
import {
  selectCsCartItems,
  selectCsCartInfo,
  selectCsCartSubTotal,
  selectCsCartTaxValue,
  selectCsCartTotalPrice,
  selectCsCartDeliveryPrice,
  selectCsCartTablePrice,
  loadCsCartItems,
  refreshCsCartItems,
} from '../features/csFlow/csCartSlice'
import { CartItem } from '../types'

// Import API hooks
import { useGetCashierMenuQuery } from '../features/menu/menuAPI'
import { useChangeCartTypeMutation } from '../features/cart/cartAPI'
import apiService from '../services/api'
const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()

  // Redux state
  const { settings, orderTaxPercentage } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)
  const { selectedCategory, searchQuery, isLoading } = useSelector((state: RootState) => state.menu)
  const { order_type, customer_id, payment_method, table_id, notes, address_id, delivery_address } = useSelector((state: RootState) => state.cart)
  const { hasCustomerData, customerId: selectedCustomerId, isModalOpen: isCustomerModalOpen } = useSelector((state: RootState) => state.customer)
  const selectedTableId = useSelector((state: RootState) => state.tables.selectedTable)

  // Get cart items from cartItemsSlice (the actual source of truth from API)
  const cartItemsRaw = useSelector(selectCartItems)
  const cartInfo = useSelector(selectCartInfo)
  const wasClearedAfterOrder = useSelector((state: RootState) => state.cartItems.wasClearedAfterOrder)

  // Get branch ID from auth (logged-in user's branch)
  // For CS users, this might be null - they use csSelectedBranchId instead
  const branchId = useSelector((state: RootState) => {
    // أولوية: استخدام branch ID من بيانات تسجيل الدخول
    const user = state.auth.user
    if (user?.branch?.id) {
      const branchId = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
      if (!isNaN(branchId) && branchId > 0) {
        return branchId
      }
    }
    // Fallback: استخدام branch ID من localStorage
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.id) {
          const branchId = typeof employeeData.branch.id === 'number'
            ? employeeData.branch.id
            : Number(employeeData.branch.id)
          if (!isNaN(branchId) && branchId > 0) {
            return branchId
          }
        }
      }
    } catch (e) {
    }
    // Return null instead of throwing - let activeBranchId handle the error case
    return null
  })
  const typeId = useSelector((state: RootState) => state.menu.userTypeId) || 1

  // API mutation for changing cart type
  const [changeCartType] = useChangeCartTypeMutation()

  // Get CS Flow state
  const isCsFlowComplete = useSelector(selectIsCsFlowComplete)
  const csCustomerId = useSelector((state: RootState) => state.csFlow.customerId)
  const csSelectedBranchId = useSelector((state: RootState) => state.csFlow.selectedBranchId)
  const csSelectedTypeId = useSelector(selectSelectedTypeId)

  // Local state - declare isCustomerService early since it's used in useMemo below
  const [isCustomerService, setIsCustomerService] = useState(false)

  // Get branch and type for menu (CS or regular) - computed with useMemo
  const activeBranchId = useMemo(() => {
    // أولوية: إذا كان هناك csSelectedBranchId (حتى لو isCustomerService لم يتم تعيينه بعد)
    // هذا مهم لأن CS user قد يكون لديه branch محدد قبل أن يتم تعيين isCustomerService
    if (csSelectedBranchId) {
      return csSelectedBranchId
    }

    // للمستخدمين CS، استخدم selectedBranchId من CS flow
    if (isCustomerService) {
      // Fallback to branchId if csSelectedBranchId is not available yet
      return branchId
    }

    // للمستخدمين العاديين (cashier)، استخدم branch ID من auth
    // If branchId is null, this means user is not properly logged in
    if (!branchId) {
      // Don't throw here - let the skip condition in useGetCashierMenuQuery handle it
      return null
    }
    return branchId
  }, [isCustomerService, csSelectedBranchId, branchId])

  const activeTypeId = useMemo(() => {
    if (isCustomerService) {
      return csSelectedTypeId
    }
    // Keep cashier menu type in sync with selected order type
    switch (order_type) {
      case 'delivery': return 1
      case 'takeaway': return 2
      case 'dine_in': return 3
      case 'car_pickup': return 4
      default: return typeId || 2
    }
  }, [isCustomerService, csSelectedTypeId, order_type, typeId])

  // Fetch menu data using the correct cashier API endpoint
  // Skip if CS user and flow is not complete - CRITICAL: Only fetch when flow is complete
  const { data: menuData, isLoading: menuLoading, error: menuError, refetch: refetchMenu } = useGetCashierMenuQuery(
    { branchId: activeBranchId, typeId: activeTypeId, isCustomerService },
    {
      skip: !activeBranchId || !activeTypeId || (isCustomerService && !isCsFlowComplete),
      refetchOnMountOrArgChange: false, // Don't refetch on mount - only when skip changes
      refetchOnFocus: false,
      refetchOnReconnect: false
    }
  )

  // RTK Query will automatically fetch menu when skip condition changes from true to false
  // No need for manual useEffect to trigger refetch - RTK Query handles it automatically

  // Process menu data
  const allProducts = React.useMemo(() => {
    if (!menuData) {
      return []
    }

    // If menuData is an object with categories property
    if (menuData && typeof menuData === 'object' && 'categories' in menuData && Array.isArray(menuData.categories)) {
      const categories = menuData.categories
      const products: any[] = []
      categories.forEach((category: any) => {
        if (category.products && Array.isArray(category.products)) {
          category.products.forEach((product: any) => {
            products.push({
              ...product,
              category: category.name,
              categoryName: category.name,
            })
          })
        }
      })
      return products
    }

    // If menuData is already an array
    if (Array.isArray(menuData)) {
      const products: any[] = []
      menuData.forEach((category: any) => {
        if (category.products && Array.isArray(category.products)) {
          category.products.forEach((product: any) => {
            products.push({
              ...product,
              category: category.name,
              categoryName: category.name,
            })
          })
        }
      })
      return products
    }

    return []
  }, [menuData])

  // Get categories from menu data
  const menuCategories = React.useMemo(() => {
    if (!menuData) return []

    // If menuData is an object with categories property
    if (menuData && typeof menuData === 'object' && 'categories' in menuData && Array.isArray(menuData.categories)) {
      const cats = menuData.categories.map((category: any) => category.name || category.name_en).filter(Boolean)
      return cats
    }

    // If menuData is already an array
    if (Array.isArray(menuData)) {
      const cats = menuData.map((category: any) => category.name || category.name_en).filter(Boolean)
      return cats
    }

    return []
  }, [menuData])


  // Local state
  const [selectedProduct, setSelectedProduct] = useState<any>(null)
  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false)
  const [isCsOrderTypeModalOpen, setIsCsOrderTypeModalOpen] = useState(false)

  // Use notes from Redux store as source of truth
  const orderNote = notes || ''

  // Get discount modal state from Redux
  const {
    isModalOpen: isDiscountModalOpenRedux,
    discountCode,
    discountAmount,
    discountPercentage,
    hasDiscount,
  } = useSelector((state: RootState) => state.discount)

  const isRTL = settings.language === 'ar'

  const extractCsPaymentLinkUrl = (payload: any, depth = 0): string | undefined => {
    if (!payload || depth > 4) return undefined

    if (typeof payload === 'string') {
      const trimmed = payload.trim()
      if (trimmed.startsWith('http')) {
        return trimmed
      }
      return undefined
    }

    if (Array.isArray(payload)) {
      for (const item of payload) {
        const result = extractCsPaymentLinkUrl(item, depth + 1)
        if (result) return result
      }
      return undefined
    }

    const searchKeys = [
      'link',
      'url',
      'payment_url',
      'paymentUrl',
      'payment_link',
      'checkout_url',
      'checkoutUrl',
      'redirect_url',
      'redirectUrl',
    ]

    for (const key of searchKeys) {
      const value = payload[key]
      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (trimmedValue.startsWith('http')) {
          return trimmedValue
        }
      }
    }

    if (payload.data) {
      return extractCsPaymentLinkUrl(payload.data, depth + 1)
    }

    return undefined
  }

  const handleCsPaymentLink = async (orderId: string | number): Promise<void> => {
    if (!orderId) return

    try {
      const formData = new FormData()
      formData.append('order_id', orderId.toString())

      const response = await apiService.axios.post(
        '/customerService/paymob/createPaymentLink',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      const paymentUrl = extractCsPaymentLinkUrl(response.data)
      if (paymentUrl) {
        if (typeof window !== 'undefined') {
          window.open(paymentUrl, '_blank', 'noopener,noreferrer')
        }
        toast.success(
          t('cs.payment_link_opened') || 'تم فتح صفحة الدفع الإلكتروني',
          { duration: 4000 }
        )
      } else {
        toast.error(
          t('cs.payment_link_missing') || 'لم يتم الحصول على رابط الدفع الإلكتروني',
          { duration: 4000 }
        )
      }
    } catch (error: any) {
      logError('Failed to create CS payment link', error)
      toast.error(
        t('cs.payment_link_failed') || 'فشل في إنشاء رابط الدفع الإلكتروني. حاول مرة أخرى.',
        { duration: 4000 }
      )
    }
  }

  // RTK Query will automatically refetch when skip condition changes
  // No need for manual refetch when modal closes - RTK Query handles it automatically


  // Check if current user is Customer Service (CS)
  useEffect(() => {
    try {
      // First try employee_data
      const employeeDataStr = localStorage.getItem('employee_data')
      let employeeData: any = null

      if (employeeDataStr) {
        employeeData = JSON.parse(employeeDataStr)
      } else {
        // Fallback to user_data (from auth)
        const userDataStr = localStorage.getItem('user_data')
        if (userDataStr) {
          const userData = JSON.parse(userDataStr)
          employeeData = {
            ...userData,
            user_type: userData.type || userData.user_type,
            type: userData.type || userData.user_type,
          }
          // Save as employee_data for future use
          localStorage.setItem('employee_data', JSON.stringify(employeeData))
        }
      }

      if (employeeData) {
        const userType = employeeData.user_type || employeeData.type || ''
        const isCS = userType.toString().toLowerCase().includes('cs') ||
          userType.toString().toLowerCase().includes('customer_service') ||
          userType.toString().toLowerCase() === 'customer_service'

        setIsCustomerService(isCS)

        // Initialize CS Flow if CS user
        if (isCS) {
          dispatch(loadOrderTypes() as any)
          dispatch(loadSavedBranchData())
        }
      } else {
        setIsCustomerService(false)
      }
    } catch (e) {
      setIsCustomerService(false)
    }
  }, [user, dispatch])

  // Get CS Cart data
  const csCartItems = useSelector(selectCsCartItems)
  const csCartInfo = useSelector(selectCsCartInfo)
  const csCartSubtotal = useSelector(selectCsCartSubTotal)
  const csCartTax = useSelector(selectCsCartTaxValue)
  const csCartTotal = useSelector(selectCsCartTotalPrice)
  const csCartDeliveryPrice = useSelector(selectCsCartDeliveryPrice)
  const csCartTablePrice = useSelector(selectCsCartTablePrice)

  // Load CS cart when customer is selected
  useEffect(() => {
    if (isCustomerService && csCustomerId > 0) {
      dispatch(loadCsCartItems({ customerId: csCustomerId, forceRefresh: false }) as any)
    }
  }, [isCustomerService, csCustomerId, dispatch])

  // Use CS cart or regular cart based on user type
  const activeCartItems = isCustomerService ? csCartItems : cartItemsRaw
  const activeCartInfo = isCustomerService ? csCartInfo : cartInfo

  const activeCartDiscountAmount = useMemo(() => {
    if (!activeCartInfo) {
      return 0
    }
    const discountValue = activeCartInfo.discount
    return parseFloat(discountValue?.toString() ?? '0') || 0
  }, [activeCartInfo?.discount])


  // Note: Discount sync removed - discount is now managed entirely by DiscountCodeInput/DiscountModal components


  // Note: activeBranchId and activeTypeId are already declared above with useMemo (lines 95-101)
  // RTK Query will automatically fetch menu when skip condition is false

  // Helper function to get tableId from multiple sources
  const getTableId = useMemo(() => {
    // Priority 1: cartSlice
    if (table_id) {
      const tableId = typeof table_id === 'number' ? table_id : Number(table_id)
      if (!isNaN(tableId) && tableId > 0) {
        return tableId
      }
    }
    // Priority 2: cartInfo
    if (cartInfo?.table_id) {
      const tableId = typeof cartInfo.table_id === 'number' ? cartInfo.table_id : Number(cartInfo.table_id)
      if (!isNaN(tableId) && tableId > 0) {
        return tableId
      }
    }
    // Priority 3: tablesSlice
    if (selectedTableId > 0) {
      return selectedTableId
    }
    return undefined
  }, [table_id, cartInfo?.table_id, selectedTableId])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleHomeLogoRefresh = () => {
      if (location.pathname !== '/home') {
        return
      }
      if (wasClearedAfterOrder) {
        return
      }

      if (isCustomerService && csCustomerId > 0) {
        dispatch(refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any)
      } else if (!isCustomerService) {
        dispatch(loadCartItems({ forceRefresh: true, tableId: getTableId }) as any)
      }
    }

    window.addEventListener('homeLogoClicked', handleHomeLogoRefresh as EventListener)

    return () => {
      window.removeEventListener('homeLogoClicked', handleHomeLogoRefresh as EventListener)
    }
  }, [dispatch, isCustomerService, csCustomerId, wasClearedAfterOrder, getTableId, location.pathname])

  // Load cart items on mount (only for non-CS users)
  // CRITICAL: Don't load if cart was cleared after order submission
  // CRITICAL: Only load when actually on home page
  useEffect(() => {
    if (location.pathname === '/home' && !isCustomerService && !wasClearedAfterOrder) {
      dispatch(loadCartItems({ forceRefresh: false, tableId: getTableId }) as any)
    }
  }, [dispatch, isCustomerService, wasClearedAfterOrder, getTableId, location.pathname])

  // Refresh cart items on page visibility change (refresh or tab focus)
  // CRITICAL: Don't refresh if cart was cleared after order submission
  // CRITICAL: Only refresh when actually on home page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (location.pathname === '/home' && document.visibilityState === 'visible' && !wasClearedAfterOrder) {
        // Page became visible (refresh or tab focus)
        if (isCustomerService && csCustomerId > 0) {
          dispatch(refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any)
        } else if (!isCustomerService) {
          dispatch(loadCartItems({ forceRefresh: true, tableId: getTableId }) as any)
        }
      }
    }

    const handleFocus = () => {
      // Window gained focus - only if cart wasn't cleared
      if (location.pathname === '/home' && !wasClearedAfterOrder) {
        if (isCustomerService && csCustomerId > 0) {
          dispatch(refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any)
        } else if (!isCustomerService) {
          dispatch(loadCartItems({ forceRefresh: true, tableId: getTableId }) as any)
        }
      }
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Listen for window focus
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [dispatch, isCustomerService, csCustomerId, wasClearedAfterOrder, location.pathname])

  // Refresh all UI elements when returning from order confirmation or when component mounts
  // CRITICAL: Don't refresh if cart was cleared after order submission
  useEffect(() => {
    // Refresh when navigating to home page
    if (location.pathname === '/home') {
      // Only refresh if cart wasn't cleared after order submission
      // The wasClearedAfterOrder flag prevents refetching old data
      if (!wasClearedAfterOrder) {
        // Refresh cart items only if cart wasn't cleared
        if (isCustomerService && csCustomerId > 0) {
          dispatch(refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any)
        } else if (!isCustomerService) {
          dispatch(loadCartItems({ forceRefresh: true, tableId: getTableId }) as any)
        }
      }

      // Reset notes
      dispatch(setNotes(''))

      // Reset search query to start fresh (but keep category selection)
      dispatch(updateSearchQuery(''))

      // Refetch menu to ensure fresh data (only if query has been started)
      // Check if we have valid branchId and typeId before refetching
      if (refetchMenu && activeBranchId && activeTypeId) {
        try {
          refetchMenu()
        } catch (error) {
          // Query might not have started yet if skip condition was true
          // This is expected and can be ignored
        }
      }
    }
  }, [location.pathname, dispatch, isCustomerService, csCustomerId, refetchMenu, wasClearedAfterOrder, activeBranchId, activeTypeId])

  // Auto-select first category when menu data loads
  useEffect(() => {
    // Auto-select first category when categories are loaded and no category is selected
    if (menuCategories && menuCategories.length > 0) {
      // If no category is selected, or if selected category doesn't exist in current categories
      const categoryExists = selectedCategory && menuCategories.includes(selectedCategory)
      if (!selectedCategory || !categoryExists) {
        dispatch(selectCategory(menuCategories[0]))
      }
    }
  }, [menuCategories, selectedCategory, dispatch])

  // Memoized filtered products for better performance
  const filteredProducts = useMemo(() => {
    if (!allProducts || allProducts.length === 0) return []

    return allProducts.filter(product => {
      const matchesCategory = !selectedCategory ||
        product.category === selectedCategory ||
        product.categoryName === selectedCategory ||
        product.category_id?.toString() === selectedCategory

      const matchesSearch = !searchQuery ||
        (product.name && product.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (product.name_en && product.name_en.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()))

      return matchesCategory && matchesSearch && product.is_available !== false
    })
  }, [allProducts, selectedCategory, searchQuery])

  // Combined loading state
  const isDataLoading = menuLoading || isLoading

  // Transform cart items from cartItemsSlice format to CartItem format
  // Get allowed order type IDs from user data
  const allowedOrderTypeIds = useMemo(() => {
    if (user?.orderTypes && Array.isArray(user.orderTypes)) {
      return user.orderTypes.map((ot: any) => ot.id).filter((id: any) => id != null)
    }
    // Try to get from localStorage as fallback
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.orderTypes && Array.isArray(employeeData.orderTypes)) {
          return employeeData.orderTypes.map((ot: any) => ot.id).filter((id: any) => id != null)
        }
      }
    } catch (e) {
    }
    return [] // Return empty array if no order types found
  }, [user?.orderTypes])

  const items: CartItem[] = useMemo(() => {
    const sourceItems = activeCartItems || []
    if (!sourceItems || sourceItems.length === 0) return []

    return sourceItems
      .filter((rawItem: any) => {
        // Filter out items without valid IDs
        const itemId = rawItem.id?.toString() || rawItem.item_id?.toString() || rawItem.product_id?.toString()
        return itemId && itemId.trim().length > 0
      })
      .map((rawItem: any) => {
        // Extract meal info if available
        const meal = rawItem.meals && Array.isArray(rawItem.meals) && rawItem.meals.length > 0
          ? rawItem.meals[0]
          : null

        // Get item ID - try multiple sources
        const itemId = rawItem.id?.toString() ||
          rawItem.item_id?.toString() ||
          rawItem.product_id?.toString() ||
          (rawItem.id ? String(rawItem.id) : '')

        // If still no ID, skip this item (shouldn't happen due to filter, but just in case)
        if (!itemId || itemId.trim().length === 0) {
          logWarning('Cart item missing ID after filtering:', rawItem)
          return null
        }

        // Get product name
        const productName = meal?.product?.name || rawItem.product_name || rawItem.name || 'Unknown Item'

        // Get image
        const image = meal?.image || rawItem.image_url || rawItem.image || ''

        // Get price
        const price = parseFloat(rawItem.unit_price || rawItem.price || '0')
        const totalPrice = parseFloat(rawItem.total_unit_price || rawItem.total_price || (price * (rawItem.quantity || 1)).toString())

        // Get quantity
        const quantity = parseInt(rawItem.quantity?.toString() || '1')

        // Get modifiers/additionals
        const modifiers = (rawItem.additionals || rawItem.extras || meal?.additionals || [])
          .filter((m: any) => m != null)
          .map((m: any) => ({
            id: m.id?.toString() || '',
            name: typeof m === 'string' ? m : (m.name || String(m)),
            price: typeof m === 'object' && m.price ? parseFloat(m.price) : 0
          }))

        return {
          id: itemId,
          product_id: rawItem.item_id?.toString() || rawItem.product_id?.toString() || itemId,
          product: {
            id: meal?.product?.id || rawItem.item_id || 0,
            name: productName,
            price: price,
            image: image,
            meals: rawItem.meals || [],
            additionalsGroups: []
          },
          quantity: quantity,
          price: price,
          total_price: totalPrice,
          modifiers: modifiers,
          meals: rawItem.meals || [],
          additionals: rawItem.additionals || []
        } as CartItem
      })
      .filter((item: CartItem | null) => item != null) as CartItem[]
  }, [activeCartItems])

  // Calculate cart totals from cartInfo or fallback to items calculation
  // Note: For CS users, these are already calculated from selectors
  const calculatedSubtotal = items.reduce((sum, item) => sum + (item.total_price || item.price * item.quantity), 0)
  const cartInfoSubtotalRaw = activeCartInfo?.sub_total
  const cartInfoSubtotal = cartInfoSubtotalRaw != null
    ? parseFloat(cartInfoSubtotalRaw.toString())
    : undefined
  const hasApiSubtotal = cartInfoSubtotal !== undefined && !isNaN(cartInfoSubtotal)
  const cartSubtotal = isCustomerService
    ? csCartSubtotal
    : (hasApiSubtotal
      ? cartInfoSubtotal!
      : calculatedSubtotal)

  // Calculate tax from API when available, otherwise fall back to calculated tax
  const taxPercentage = parseFloat(activeCartInfo?.tax_percentage?.toString() ?? '') || orderTaxPercentage || 0
  const calculatedTax = calculatedSubtotal * (taxPercentage / 100)
  const cartInfoTaxRaw = activeCartInfo?.tax_value
  const cartInfoTax = cartInfoTaxRaw != null
    ? parseFloat(cartInfoTaxRaw.toString())
    : undefined
  const hasApiTax = cartInfoTax !== undefined && !isNaN(cartInfoTax)
  const cartTax = isCustomerService
    ? csCartTax
    : (hasApiTax
      ? cartInfoTax!
      : calculatedTax)

  // Get table price from cartInfo
  const cartTablePrice = isCustomerService
    ? csCartTablePrice
    : (activeCartInfo?.table_price
      ? parseFloat(activeCartInfo.table_price.toString())
      : 0)

  const cartDeliveryPrice = isCustomerService
    ? csCartDeliveryPrice
    : (activeCartInfo?.delivery_price
      ? parseFloat(activeCartInfo.delivery_price.toString())
      : 0)

  const cartServicePriceRaw = activeCartInfo?.service_price
  const parsedCartServicePrice = cartServicePriceRaw != null && cartServicePriceRaw !== ''
    ? parseFloat(cartServicePriceRaw.toString())
    : NaN
  const cartServicePrice = Number.isFinite(parsedCartServicePrice) ? parsedCartServicePrice : 0

  // Calculate total items count (prefer API value when present)
  const calculatedItemsCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0)
  const cartInfoItemsCountRaw = activeCartInfo?.items_count
  const cartInfoItemsCount = cartInfoItemsCountRaw != null
    ? parseInt(cartInfoItemsCountRaw.toString())
    : undefined
  const hasApiItemsCount = cartInfoItemsCount !== undefined && !isNaN(cartInfoItemsCount)
  const totalItemsCount = isCustomerService
    ? (activeCartInfo?.items_count ? parseInt(activeCartInfo.items_count.toString()) || 0 : 0)
    : (hasApiItemsCount
      ? cartInfoItemsCount!
      : calculatedItemsCount)

  // Handle category selection
  const handleCategorySelect = (categoryId: string | number | null) => {
    dispatch(selectCategory(categoryId?.toString() || ''))
  }

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(updateSearchQuery(query))
  }


  // Helper function to convert order type to typeId
  const getTypeIdFromOrderType = (orderType: 'delivery' | 'takeaway' | 'dine_in' | 'car_pickup'): number => {
    switch (orderType) {
      case 'delivery': return 1
      case 'takeaway': return 2
      case 'dine_in': return 3
      case 'car_pickup': return 4
      default: return 3
    }
  }

  // Handle order type change
  const handleOrderTypeChange = async (orderType: 'delivery' | 'takeaway' | 'dine_in' | 'car_pickup') => {
    const newTypeId = getTypeIdFromOrderType(orderType)

    // Update local state first
    dispatch(setOrderTypeLocally(orderType))
    dispatch(setUserTypeId(newTypeId))

    // Clear delivery address when leaving delivery
    if (orderType !== 'delivery') {
      dispatch(clearDeliveryAddressLocally())
    }

    // Selecting delivery: if customer exists but no address → open address popup
    if (orderType === 'delivery' && !isCustomerService) {
      const hasAddress = Boolean(address_id) || Boolean(delivery_address?.id) || Boolean(cartInfo?.address_id)
      if (hasCustomerData && !hasAddress) {
        setIsAddressModalOpen(true)
      } else if (!hasCustomerData) {
        setShouldOpenAddressModalAfterCustomer(true)
        setShowAddCustomerAlert(true)
      }
    }

    const deliveryAddressId = orderType === 'delivery'
      ? Number(address_id || delivery_address?.id || cartInfo?.address_id) || undefined
      : undefined

    // Delivery requires addressId; AddressSelectionModal sends change-type once an address is chosen
    if (orderType === 'delivery' && !deliveryAddressId) {
      return
    }

    // If there are items in the cart, update cart type via API
    if (activeCartItems && activeCartItems.length > 0 && activeCartInfo?.id) {
      try {
        const customerUserId = selectedCustomerId
          ? Number(selectedCustomerId)
          : (customer_id ? Number(customer_id) : undefined)
        const addressId = deliveryAddressId

        await changeCartType({
          cartId: activeCartInfo.id,
          typeId: newTypeId,
          branchId: activeBranchId,
          userId: customerUserId,
          addressId,
        }).unwrap()

        // Reload cart items to reflect the change
        if (isCustomerService && csCustomerId > 0) {
          dispatch(refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any)
        } else {
          dispatch(loadCartItems({ forceRefresh: true, tableId: getTableId }) as any)
        }
      } catch (error) {
        toast.error(t('cs.failed_to_update_order_type'), {
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '16px',
            fontFamily: 'IBM Plex Sans Arabic, sans-serif',
            direction: 'rtl',
          },
        })
      }
    }
  }

  // Handle table selection

  // Handle product selection for details modal
  const handleProductSelect = (product: any) => {
    setSelectedProduct(product)
    setIsProductModalOpen(true)
  }

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false)
    setSelectedProduct(null)
  }

  // Notes modal handlers
  const openNotesModal = () => setIsNotesModalOpen(true)
  const closeNotesModal = () => setIsNotesModalOpen(false)
  const handleSaveNote = (note: string) => {
    dispatch(setNotes(note))
  }

  // Discount modal handlers
  const openDiscountModal = () => {
    dispatch(openModal())
    setIsDiscountModalOpen(true)
  }
  const closeDiscountModal = () => {
    setIsDiscountModalOpen(false)
  }

  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false)
  const [isEvaluatingOffer, setIsEvaluatingOffer] = useState(false)
  const [showOfferConfirmation, setShowOfferConfirmation] = useState(false)
  const [pendingOfferEvaluation, setPendingOfferEvaluation] =
    useState<PromotionEvaluation | null>(null)
  const [offerPayableTotal, setOfferPayableTotal] = useState(0)
  const [offerPriceBeforeDiscount, setOfferPriceBeforeDiscount] = useState(0)

  const submitOrderCore = useCallback(async () => {
    setIsSubmittingOrder(true)

    try {
      const createResult = await dispatch(createOrder() as any)

      if (createOrder.fulfilled.match(createResult)) {
        const orderId = createResult.payload?.orderId
        const orderData = createResult.payload?.data

        let finalOrderId = orderId
        if (!finalOrderId && orderData) {
          finalOrderId = (orderData as any)?.id ||
            (orderData as any)?.order_id ||
            (orderData as any)?.orderId
        }

        if (!finalOrderId) {
          if (orderData && typeof orderData === 'object') {
            const findIdRecursive = (obj: any): string | number | undefined => {
              if (!obj || typeof obj !== 'object') return undefined
              if (obj.id) return obj.id
              if (obj.order_id) return obj.order_id
              if (obj.orderId) return obj.orderId
              for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                  const found = findIdRecursive(obj[key])
                  if (found) return found
                }
              }
              return undefined
            }
            finalOrderId = findIdRecursive(orderData)
          }

          if (!finalOrderId) {
            throw new Error('Order ID not returned')
          }
        }

        if (payment_method === 'online') {
          createPaymobPaymentLinkInBackground(finalOrderId)
            .then((result) => {
              if (result.success) {
                log('Paymob payment link created successfully:', result.paymentLink)
              } else {
                logError('Failed to create Paymob payment link:', result.error)
              }
            })
            .catch((error) => {
              logError('Error creating Paymob payment link:', error)
            })
        }

        await clearAllDataAfterSuccessfulOrder(dispatch)

        if (payment_method === 'partial') {
          navigate(`/partial-payment/${finalOrderId}`, {
            replace: false
          })
        } else {
          const shouldAutoPrint = !isCustomerService

          try {
            const detailsResult = await dispatch(fetchOrderDetails(finalOrderId) as any)

            if (fetchOrderDetails.fulfilled.match(detailsResult)) {
              navigate('/order-confirmation', {
                state: {
                  orderId: finalOrderId,
                  orderData: detailsResult.payload || orderData,
                  shouldAutoPrint: shouldAutoPrint
                },
                replace: false
              })
            } else {
              navigate('/order-confirmation', {
                state: {
                  orderId: finalOrderId,
                  orderData: orderData,
                  shouldAutoPrint: shouldAutoPrint
                },
                replace: false
              })
            }
          } catch (fetchError) {
            navigate('/order-confirmation', {
              state: {
                orderId: finalOrderId,
                orderData: orderData,
                shouldAutoPrint: shouldAutoPrint
              },
              replace: false
            })
          }
        }
      } else if (createOrder.rejected.match(createResult)) {
        const errorPayload = createResult.payload as any

        log('Order creation error payload:', errorPayload)

        let errorMsg = t('error.order_submission_failed')
        let descriptiveMessage = ''

        if (typeof errorPayload === 'string') {
          errorMsg = errorPayload
        } else if (errorPayload && typeof errorPayload === 'object') {
          errorMsg = errorPayload.message || errorMsg

          descriptiveMessage =
            errorPayload.descriptiveMessage ||
            (errorPayload.errorData && typeof errorPayload.errorData === 'object'
              ? errorPayload.errorData.descriptiveMessage
              : '') ||
            ''
        }

        const isSessionError =
          descriptiveMessage === 'session_not_belong_to_user' ||
          errorMsg === 'session_not_belong_to_user' ||
          errorPayload?.errorData?.descriptiveMessage === 'session_not_belong_to_user' ||
          errorPayload?.descriptiveMessage === 'session_not_belong_to_user' ||
          (typeof errorPayload === 'object' && errorPayload?.errorData && errorPayload.errorData.descriptiveMessage === 'session_not_belong_to_user')

        if (isSessionError) {
          toast.error(
            t('table.busy_table_error'),
            {
              duration: 6000,
              style: {
                background: '#f44336',
                color: '#fff',
                fontSize: '16px',
                fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
                direction: isRTL ? 'rtl' : 'ltr',
                padding: '16px 24px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                fontWeight: '500',
                maxWidth: '500px',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#f44336',
              },
            }
          )
          setIsSubmittingOrder(false)
          return
        }

        const displayMessage = descriptiveMessage || errorMsg
        toast.error(displayMessage, {
          duration: 6000,
          style: {
            background: '#f44336',
            color: '#fff',
            fontSize: '16px',
            fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
            direction: isRTL ? 'rtl' : 'ltr',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
            fontWeight: '500',
            maxWidth: '500px',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#f44336',
          },
        })
        setIsSubmittingOrder(false)
        return
      }
    } catch (error: any) {
      const errorData = error?.response?.data || error?.errorData || {}
      const descriptiveMessage =
        errorData.descriptiveMessage ||
        error?.descriptiveMessage ||
        (typeof error?.message === 'string' && error.message.includes('session_not_belong_to_user') ? 'session_not_belong_to_user' : null)

      const errorMessage = error?.message || ''
      const isSessionError =
        descriptiveMessage === 'session_not_belong_to_user' ||
        errorMessage.includes('session_not_belong_to_user') ||
        errorData.descriptiveMessage === 'session_not_belong_to_user'

      if (isSessionError) {
        toast.error(
          t('table.busy_table_error'),
          {
            duration: 6000,
            style: {
              background: '#f44336',
              color: '#fff',
              fontSize: '16px',
              fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
              direction: isRTL ? 'rtl' : 'ltr',
              padding: '16px 24px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
              fontWeight: '500',
              maxWidth: '500px',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#f44336',
            },
          }
        )
      } else {
        const displayMessage = descriptiveMessage || errorMessage || t('error.order_submission_failed')
        toast.error(displayMessage, {
          duration: 6000,
          style: {
            background: '#f44336',
            color: '#fff',
            fontSize: '16px',
            fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
            direction: isRTL ? 'rtl' : 'ltr',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
            fontWeight: '500',
            maxWidth: '500px',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#f44336',
          },
        })
      }
    } finally {
      setIsSubmittingOrder(false)
    }
  }, [dispatch, isCustomerService, isRTL, navigate, payment_method, t])

  const handleOfferConfirm = useCallback(async () => {
    setShowOfferConfirmation(false)
    setPendingOfferEvaluation(null)
    if (isSubmittingOrder || isEvaluatingOffer) return
    await submitOrderCore()
  }, [isEvaluatingOffer, isSubmittingOrder, submitOrderCore])

  const handleOfferCancel = useCallback(() => {
    setShowOfferConfirmation(false)
    setPendingOfferEvaluation(null)
    setOfferPriceBeforeDiscount(0)
  }, [])

  // Handle send order
  const handleSendOrder = async () => {
    // Validation
    if (items.length === 0) {
      toast.error(t('cart.cart_empty'), {
        duration: 4000,
        style: {
          background: '#363636',
          color: '#fff',
          fontSize: '16px',
          fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
          direction: isRTL ? 'rtl' : 'ltr',
        },
      })
      return
    }

    // For dine_in orders, check customer and table requirements
    if (order_type === 'dine_in') {
      // If no customer, show alert modal with options (add customer or change to takeaway)
      if (!hasCustomerData) {
        setShowAddCustomerAlert(true)
        return
      }

      // If customer exists but no table, open table selection modal
      // Check both cart.table_id and tables.selectedTable to ensure table is selected
      const hasTable = table_id || selectedTableId > 0 || cartInfo?.table_id
      if (!hasTable) {
        // Close any open customer alert modal
        setShowAddCustomerAlert(false)
        setIsTableModalOpen(true)
        return
      }
    }

    // For delivery orders, require customer + address
    if (order_type === 'delivery' && !isCustomerService) {
      if (!hasCustomerData) {
        setShowAddCustomerAlert(true)
        return
      }

      const hasAddress = Boolean(address_id) || Boolean(delivery_address?.id) || Boolean(cartInfo?.address_id)
      if (!hasAddress) {
        setShowAddCustomerAlert(false)
        setIsAddressModalOpen(true)
        return
      }
    }

    // Check if payment method is online/network - require payment machine connection
    if (payment_method === 'online' && !isCustomerService) {
      toast.error(t('payment.connect_payment_machine'), {
        duration: 5000,
        style: {
          background: '#363636',
          color: '#fff',
          fontSize: '16px',
          fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
          direction: isRTL ? 'rtl' : 'ltr',
        },
      })
      return
    }

    if (isSubmittingOrder || isEvaluatingOffer) return

    setIsEvaluatingOffer(true)
    try {
      const evalResult = await dispatch(evaluateCartOffer() as any)
      const evaluation = evaluateCartOffer.fulfilled.match(evalResult)
        ? evalResult.payload!
        : emptyPromotionEvaluation

      if (evaluation.hasEligibleOffer) {
        const apiTotalRaw = isCustomerService ? csCartTotal : activeCartInfo?.total_price
        const parsedApiTotal =
          apiTotalRaw != null ? parseFloat(String(apiTotalRaw)) : NaN
        const payableTotal =
          evaluation.finalAmount != null
            ? evaluation.finalAmount
            : !Number.isNaN(parsedApiTotal)
              ? parsedApiTotal
              : 0

        const beforeDiscountRaw =
          (activeCartInfo as any)?.price_before_discount ??
          (activeCartInfo as any)?.priceBeforeDiscount ??
          activeCartInfo?.sub_total
        const parsedBeforeDiscount =
          beforeDiscountRaw != null ? parseFloat(String(beforeDiscountRaw)) : NaN
        const priceBeforeDiscount = !Number.isNaN(parsedBeforeDiscount)
          ? parsedBeforeDiscount
          : cartSubtotal

        setPendingOfferEvaluation(evaluation)
        setOfferPayableTotal(payableTotal)
        setOfferPriceBeforeDiscount(priceBeforeDiscount)
        setShowOfferConfirmation(true)
        return
      }
    } finally {
      setIsEvaluatingOffer(false)
    }

    await submitOrderCore()
  }

  // Note: isCsFlowComplete is already declared above using useSelector (line 89)

  // Local state: table selection modal
  const [isTableModalOpen, setIsTableModalOpen] = useState(false)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)

  // Local state: alert modals
  const [showAddCustomerAlert, setShowAddCustomerAlert] = useState(false)
  const [showAddProductsAlert, setShowAddProductsAlert] = useState(false)

  // Local state: payment method modal
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false)

  // Track if we need to open table/address modal after customer is added
  const [shouldOpenTableModalAfterCustomer, setShouldOpenTableModalAfterCustomer] = useState(false)
  const [shouldOpenAddressModalAfterCustomer, setShouldOpenAddressModalAfterCustomer] = useState(false)

  // Handle table selection with validation
  const handleTableSelectClick = () => {
    // First check: Is there a customer?
    if (!hasCustomerData) {
      setShowAddCustomerAlert(true)
      return
    }

    // Second check: Are there products in the cart?
    if (items.length === 0) {
      setShowAddProductsAlert(true)
      return
    }

    // Both checks passed, close any open customer alert and open table selection modal
    setShowAddCustomerAlert(false)
    setIsTableModalOpen(true)
  }

  const handleAddressSelectClick = () => {
    if (!hasCustomerData) {
      setShouldOpenAddressModalAfterCustomer(true)
      setShowAddCustomerAlert(true)
      return
    }

    setShowAddCustomerAlert(false)
    setIsAddressModalOpen(true)
  }

  const handleRequireDeliverySetup = useCallback((reason: 'customer' | 'address') => {
    if (reason === 'customer') {
      setShouldOpenAddressModalAfterCustomer(true)
      setShowAddCustomerAlert(true)
      return
    }
    // Customer exists — open the same address popup as "اختر عنوان التوصيل"
    setShowAddCustomerAlert(false)
    setIsAddressModalOpen(true)
  }, [])

  // Handle add customer from alert modal
  const handleAddCustomerFromAlert = () => {
    setShowAddCustomerAlert(false)
    if (order_type === 'delivery') {
      setShouldOpenAddressModalAfterCustomer(true)
    } else {
      setShouldOpenTableModalAfterCustomer(true)
    }
    dispatch(openCustomerModal())
  }

  // Handle change to takeaway from alert modal
  const handleChangeToTakeaway = async () => {
    setShowAddCustomerAlert(false)
    // Change order type to takeaway
    await handleOrderTypeChange('takeaway')
  }

  // Monitor customer addition and open table modal if needed
  useEffect(() => {
    // Only open table modal if customer modal is closed (customer was added and modal closed)
    // Check both cart.table_id and tables.selectedTable to ensure table is selected
    const hasTable = table_id || selectedTableId > 0 || cartInfo?.table_id
    if (shouldOpenTableModalAfterCustomer && hasCustomerData && !isCustomerModalOpen && order_type === 'dine_in' && !hasTable) {
      // Customer was added and modal is closed, now open table selection modal
      setShouldOpenTableModalAfterCustomer(false)
      setIsTableModalOpen(true)
    }
  }, [shouldOpenTableModalAfterCustomer, hasCustomerData, isCustomerModalOpen, order_type, table_id, selectedTableId, cartInfo?.table_id])

  // After customer is added for delivery → open address selection popup
  useEffect(() => {
    if (isCustomerService || order_type !== 'delivery') return
    if (!hasCustomerData || isCustomerModalOpen) return

    const hasAddress = Boolean(address_id) || Boolean(delivery_address?.id) || Boolean(cartInfo?.address_id)
    if (hasAddress) return

    if (shouldOpenAddressModalAfterCustomer) {
      setShouldOpenAddressModalAfterCustomer(false)
      setIsAddressModalOpen(true)
    }
  }, [
    shouldOpenAddressModalAfterCustomer,
    hasCustomerData,
    isCustomerModalOpen,
    order_type,
    isCustomerService,
    address_id,
    delivery_address?.id,
    cartInfo?.address_id,
  ])

  // Also open address popup when customer is newly set while delivery is active
  // (covers adding customer from header, not only from the alert)
  const prevHasCustomerDataRef = React.useRef(hasCustomerData)
  useEffect(() => {
    const wasMissing = !prevHasCustomerDataRef.current
    prevHasCustomerDataRef.current = hasCustomerData

    if (
      wasMissing &&
      hasCustomerData &&
      !isCustomerModalOpen &&
      !isCustomerService &&
      order_type === 'delivery'
    ) {
      const hasAddress = Boolean(address_id) || Boolean(delivery_address?.id) || Boolean(cartInfo?.address_id)
      if (!hasAddress) {
        setIsAddressModalOpen(true)
      }
    }
  }, [
    hasCustomerData,
    isCustomerModalOpen,
    isCustomerService,
    order_type,
    address_id,
    delivery_address?.id,
    cartInfo?.address_id,
  ])

  const deliveryCustomerId = useMemo(() => {
    if (selectedCustomerId != null && Number(selectedCustomerId) > 0) {
      return Number(selectedCustomerId)
    }
    if (customer_id != null && Number(customer_id) > 0) {
      return Number(customer_id)
    }
    return null
  }, [selectedCustomerId, customer_id])

  return (
    <div className="flex h-screen bg-[#F5F5F5] overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Left Side - Content Area (Menu) */}
      <div className="flex-1 h-full flex flex-col overflow-hidden relative">


        {/* Top Bar */}
        <div className="mt-2">
          <TopBar userName={user?.name} />
        </div>

        <div className={`${isRTL ? 'pr-4 pl-0' : 'pl-4 pr-0'}`}>
          {/* Search Bar */}
          <SearchBarSection
            searchQuery={searchQuery}
            onSearchChange={handleSearch}
          />

        </div>


        {/* For CS users: Show placeholder if flow not complete, otherwise show menu */}
        {isCustomerService && !isCsFlowComplete ? (
          <div className="flex-1 flex items-center justify-center">
            <CSPlaceholder isRTL={isRTL} />
          </div>
        ) : (
          <>
            {/* Category Tabs */}
            <CategoryTabs
              categories={menuCategories}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
              isLoading={isDataLoading}
            />

            {/* Products Grid */}
            <ProductsGridSection
              products={filteredProducts}
              isLoading={isDataLoading}
              currency={settings.currency}
              onProductSelect={handleProductSelect}
            />
          </>
        )}
      </div>

      {/* Right Sidebar - Cart Section (448px) */}
      <div className="w-[420px] h-full flex flex-col gap-1 p-4">
        {/* Customer Info Header - for all users */}
        <CustomerInfoHeader
          isCustomerService={isCustomerService}
          isCsFlowComplete={isCsFlowComplete}
          onSelectOrderDetails={() => {
            setIsCsOrderTypeModalOpen(true)
          }}
        />




        {/* Order Type Toggle - for non-CS users only */}
        {!isCustomerService && (
          <div className="-mt-1">
            <OrderTypeToggle
              orderType={order_type || 'takeaway'}
              onOrderTypeChange={handleOrderTypeChange}
              allowedOrderTypes={allowedOrderTypeIds}
            />
          </div>
        )}

        {/* Table Selector: show only for dine-in (محلي) */}
        {order_type === 'dine_in' && (
          <TableSelector onTableSelect={handleTableSelectClick} />
        )}

        {/* Address Selector: show only for delivery (توصيل) for cashiers */}
        {!isCustomerService && order_type === 'delivery' && (
          <AddressSelector onAddressSelect={handleAddressSelectClick} />
        )}

        {/* Cart Items List - Scrollable with Fixed Height */}
        <div className="flex-1 min-h-0 overflow-hidden overflow-x-hidden">
          <CartItemsSection
            items={items}
            currency={settings.currency}
          />
        </div>

        {/* Discount & Notes */}
        <DiscountNotesSection
          onNotesClick={openNotesModal}
          onDiscountClick={openDiscountModal}
          currentNote={orderNote}
        />

        {/* Order Summary & Send Order */}
        <OrderSummarySection
          subtotal={cartSubtotal}
          tax={cartTax}
          currency={settings.currency}
          taxPercentage={activeCartInfo?.tax_percentage}
          tablePrice={cartTablePrice}
          deliveryPrice={cartDeliveryPrice}
          servicePrice={cartServicePrice}
          totalItems={totalItemsCount}
          discountAmount={activeCartDiscountAmount}
          onSendOrder={handleSendOrder}
          onPaymentMethod={() => setIsPaymentMethodModalOpen(true)}
        />
      </div>

      {/* Product Details Modal */}
      {selectedProduct && (
        <ProductDetailsModal
          isOpen={isProductModalOpen}
          onClose={handleCloseProductModal}
          product={selectedProduct}
          onRequireDeliverySetup={handleRequireDeliverySetup}
        />
      )}

      {/* Notes Modal */}
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={closeNotesModal}
        initialNote={orderNote}
        onSave={handleSaveNote}
        isRTL={isRTL}
      />

      {/* Discount Modal */}
      <DiscountModal
        isOpen={isDiscountModalOpen || isDiscountModalOpenRedux}
        onClose={closeDiscountModal}
        isRTL={isRTL}
      />

      {/* Table Selection Modal */}
      <TableSelectionModal
        isOpen={isTableModalOpen}
        onClose={() => setIsTableModalOpen(false)}
      />

      {/* Address Selection Modal - cashier delivery */}
      {!isCustomerService && deliveryCustomerId != null && activeBranchId && (
        <AddressSelectionModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          userId={deliveryCustomerId}
          branchId={Number(activeBranchId)}
        />
      )}

      {/* Add Customer Alert Modal */}
      <AddCustomerAlertModal
        isOpen={showAddCustomerAlert}
        onClose={() => setShowAddCustomerAlert(false)}
        onAddCustomer={handleAddCustomerFromAlert}
        onChangeToTakeaway={handleChangeToTakeaway}
        variant={order_type === 'delivery' ? 'delivery' : 'dine_in'}
      />

      {/* Add Products Alert Modal */}
      <AddProductsAlertModal
        isOpen={showAddProductsAlert}
        onClose={() => setShowAddProductsAlert(false)}
      />

      {/* Payment Method Selection Modal */}
      <PaymentMethodModal
        isOpen={isPaymentMethodModalOpen}
        onClose={() => setIsPaymentMethodModalOpen(false)}
        showPartialPayment={!isCustomerService}
        isCustomerService={isCustomerService}
        onSelectPaymentMethod={(method) => {
          // Map method to cart payment method format
          const paymentMethodMap: Record<string, 'cash' | 'card' | 'online' | 'partial'> = {
            'cash': 'cash',
            'network': 'online',
            'partial': 'partial'
          }
          const mappedMethod = paymentMethodMap[method] || 'cash'
          dispatch(setPaymentMethod(mappedMethod))
        }}
      />

      {/* CS Order Type Selection Modal */}
      {isCustomerService && (
        <CsOrderTypeSelectionPopup
          isOpen={isCsOrderTypeModalOpen}
          onClose={() => {
            setIsCsOrderTypeModalOpen(false)
            // RTK Query will automatically fetch menu when skip condition changes
          }}
        />
      )}

      {/* Offer confirmation before order creation */}
      {pendingOfferEvaluation && (
        <OfferConfirmationModal
          isOpen={showOfferConfirmation}
          evaluation={pendingOfferEvaluation}
          payableTotal={offerPayableTotal}
          priceBeforeDiscount={offerPriceBeforeDiscount}
          onConfirm={handleOfferConfirm}
          onCancel={handleOfferCancel}
        />
      )}

      {/* Order Submission Loading Overlay */}
      <OrderSubmissionLoading
        isOpen={isSubmittingOrder || isEvaluatingOffer}
        isRTL={isRTL}
      />
    </div>
  )
}

export default HomePage
